import { template } from "@ember/template-compiler";
import { buildServiceFacetGroups } from 'client-portal/utils/clinicianSearch/service-facet-groups';
import { commaSeparatedStringsForQueryParam, parseCommaSeparatedStrings } from 'client-portal/utils/clinicianSearch/comma-query-param';
import { cptFlagDescriptors } from 'client-portal/utils/clinicianSearch/service-facet-helpers/flag-descriptors';
import { facetOptionDisabled } from 'client-portal/utils/clinicianSearch/facet-catalog';
import { fn } from '@ember/helper';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import RequestClinicianSearchFiltersFilterRadioLine from '../filters/filter-radio-line';
import RequestClinicianSearchFiltersPanel from '../filters/panel';
import styles from './services.module.scss';
import t from 'ember-intl/helpers/t';
const RADIO_NAME = 'clinician-search-service-filter';
const FILTER_DETAIL = 'booking.clinicianSearch.filterDetail';
export default class RequestClinicianSearchFacetFiltersServices extends Component {
    @service
    intl;
    @service
    store;
    @service
    clinicianSearch;
    styles = styles;
    radioName = RADIO_NAME;
    @tracked
    groups = [];
    @tracked
    loadFailed = false;
    @tracked
    isLoadingGroups = true;
    get selectedCptCodesCsv() {
        return (commaSeparatedStringsForQueryParam(parseCommaSeparatedStrings(this.clinicianSearch.query.cptCodes ?? '')) ?? '');
    }
    get flatOptions() {
        let out1 = [];
        for (let g1 of this.groups){
            let heading1 = g1.heading ?? '';
            for (let r1 of g1.rates ?? []){
                let ids1 = r1.allRateIds ?? (r1.key ? [
                    r1.key
                ] : []);
                let cptCodesCsv1 = commaSeparatedStringsForQueryParam(ids1) ?? '';
                out1.push({
                    key: r1.key,
                    cptRateIds: ids1,
                    cptCodesCsv: cptCodesCsv1,
                    docCount: Number(r1.docCount ?? 0),
                    description: r1.description ?? heading1 ?? '',
                    duration: r1.duration,
                    rate: r1.rate,
                    callToBook: r1.callToBook,
                    requireCreditCardForNewClients: r1.requireCreditCardForNewClients,
                    flagged: r1.flagged ?? [],
                    groupBucketIdsCsv: cptCodesCsv1
                });
            }
        }
        return out1;
    }
    optionPrimaryText = (opt1)=>{
        let desc1 = String(opt1.description ?? '').trim();
        return desc1 || this.intl.t('booking.clinicianSearch.filterDetail.cptGroupFallback');
    };
    optionFlaggedLines = (opt1)=>cptFlagDescriptors(opt1, opt1.flagged ?? []).map((d1)=>this.formatFlagLine(d1));
    flagLineLeaf = (descriptor1)=>{
        switch(descriptor1.kind){
            case 'duration':
                return [
                    'cptFlagDurationLine',
                    {
                        minutes: this.intl.t(`${FILTER_DETAIL}.cptDurationMinutes`, {
                            count: descriptor1.minutes
                        })
                    }
                ];
            case 'rate':
                return [
                    'cptFlagRateLine',
                    {
                        rate: descriptor1.rate
                    }
                ];
            case 'callToBook':
                return [
                    descriptor1.yes ? 'cptFlagCallToBookYes' : 'cptFlagCallToBookNo'
                ];
            case 'requireCreditCardForNewClients':
                return [
                    'cptFlagCredit',
                    {
                        state: descriptor1.state
                    }
                ];
            /* istanbul ignore next -- descriptors always come from cptFlagDescriptors known kinds */ default:
                return null;
        }
    };
    formatFlagLine = (descriptor1)=>{
        let leaf1 = this.flagLineLeaf(descriptor1);
        /* istanbul ignore next -- flagLineLeaf default is unreachable for cptFlagDescriptors output */ if (!leaf1) {
            return null;
        }
        let [leafKey1, values1] = leaf1;
        return this.intl.t(`${FILTER_DETAIL}.${leafKey1}`, values1);
    };
    optionDisabled = (opt1)=>facetOptionDisabled(opt1.docCount, this.isOptionSelected(opt1));
    serviceOptionRowTitle = (opt1)=>{
        if (!this.optionDisabled(opt1)) {
            return '';
        }
        return this.intl.t('booking.clinicianSearch.filterDetail.disabledFilterOption');
    };
    isOptionSelected = (opt1)=>this.selectedCptCodesCsv === opt1.cptCodesCsv;
    selectService = (opt1, event1)=>{
        /* istanbul ignore next -- radios always click as checked and keys are never empty */ if (!event1.target.checked || !opt1.cptRateIds?.length) {
            return;
        }
        this.clinicianSearch.query.applyFacetFilter('service', opt1.cptRateIds);
        this.clinicianSearch.mobile.backFromDetail();
    };
    refreshGroups = async ()=>{
        this.loadFailed = false;
        this.isLoadingGroups = true;
        try {
            let routeParams1 = {
                ...this.clinicianSearch.query.toRouteParams(),
                cptCodes: null
            };
            let meta1 = this.clinicianSearch.sendAll.buildFacetsMeta(routeParams1);
            this.groups = buildServiceFacetGroups(this.store, meta1?.facets?.cptCodeRates ?? [], {
                includeZeroDocCounts: true
            });
        } catch  {
            this.loadFailed = true;
            this.groups = [];
        } finally{
            this.isLoadingGroups = false;
        }
    };
    get panelLoading() {
        return this.isLoadingGroups || this.clinicianSearch.sendAll.isLoading;
    }
    get serviceLoadingAriaLabel() {
        if (this.clinicianSearch.sendAll.isLoading) {
            return this.intl.t('booking.clinicianSearch.filterDetail.filterOptionsLoading');
        }
        return this.intl.t('booking.clinicianSearch.filterDetail.serviceRatesLoading');
    }
    static{
        template(`
    <RequestClinicianSearchFiltersPanel
      @hideApply={{true}}
      @class="{{this.styles.component}} clinician-search-filter-service-options"
      @facetKey="service"
      @isLoading={{this.panelLoading}}
      @loadingAriaLabel={{this.serviceLoadingAriaLabel}}
      @onLoad={{this.refreshGroups}}
      data-test-clinician-search-filter-service-options
    >
      <p class="filter-section-title" id="clinician-search-service-filter-title">{{t
          "booking.clinicianSearch.filterDetail.servicesFilterPanelTitle"
        }}</p>
      {{#if this.loadFailed}}
        <p class="error">{{t "booking.clinicianSearch.filterDetail.serviceLoadError"}}</p>
      {{/if}}
      <div class="body" role="group" aria-labelledby="clinician-search-service-filter-title">
        <ul class="filter-checkbox-list" role="list">
          {{#each this.flatOptions as |opt|}}
            <RequestClinicianSearchFiltersFilterRadioLine
              @kind="service"
              @optionId={{opt.key}}
              @docCount={{opt.docCount}}
              @name={{this.radioName}}
              @value={{opt.key}}
              @bucketIdsCsv={{opt.groupBucketIdsCsv}}
              @checked={{this.isOptionSelected opt}}
              @disabled={{this.optionDisabled opt}}
              @title={{this.serviceOptionRowTitle opt}}
              @onChange={{fn this.selectService opt}}
            >
              <span class="service-option-primary">{{this.optionPrimaryText opt}}</span>
              <span class="service-option-secondary">
                {{#each (this.optionFlaggedLines opt) as |line|}}
                  <span class="service-option-secondary-line">{{line}}</span>
                {{/each}}
              </span>
            </RequestClinicianSearchFiltersFilterRadioLine>
          {{/each}}
        </ul>
      </div>
    </RequestClinicianSearchFiltersPanel>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
